import React from 'react';
import {
  Box,
  Button,
  Heading,
  HStack,
  Icon,
  Image,
  ListItem,
  Text,
  UnorderedList,
  VStack,
} from '@chakra-ui/react';
import { FaCalendarAlt } from 'react-icons/fa';
import { MUNLIGHT } from './munlightTheme';

/**
 * Card concerto per la rassegna Munlight Sonata (presentazionale).
 * Palette: testo chiaro su fondo ardesia; barra data viola come volantino A3.
 */
function MunlightConcertCard({ concert }) {
  const { id: concertId } = concert;
  const {
    imageSrc,
    imageAlt,
    title,
    subtitle,
    audience,
    description,
    formation,
    dateLabel,
    timeLabel,
    ctaLabel,
    ctaHref,
  } = concert;

  const paragraphs = Array.isArray(description) ? description : [description];

  const ctaIsHash = ctaHref?.startsWith('#');

  return (
    <Box
      as="article"
      borderRadius="xl"
      overflow="hidden"
      bg="rgba(255,255,255,0.06)"
      borderWidth="1px"
      borderColor="rgba(255,255,255,0.14)"
      transition="border-color 0.2s, box-shadow 0.2s"
      _hover={{
        borderColor: 'rgba(255,255,255,0.22)',
        boxShadow: `0 16px 48px rgba(0,0,0,0.35)`,
      }}
    >
      <Box
        position="relative"
        aspectRatio={16 / 9}
        overflow="hidden"
        bg={MUNLIGHT.bgSlateDeep}
      >
        <Image
          src={imageSrc}
          alt={imageAlt}
          w="100%"
          h="100%"
          objectFit="cover"
          loading="lazy"
        />
      </Box>

      <VStack align="stretch" spacing={5} p={{ base: 5, md: 7 }}>
        <Box>
          <Heading
            as="h3"
            fontSize={{ base: 'xl', md: '2xl' }}
            fontWeight="600"
            fontFamily="system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
            lineHeight="short"
            color={MUNLIGHT.white}
            mb={2}
            letterSpacing="-0.02em"
          >
            {title}
          </Heading>
          <Text fontSize="md" color={MUNLIGHT.accentSoft} fontWeight="600">
            {subtitle}
          </Text>
        </Box>

        {audience ? (
          <Text
            fontSize="sm"
            color="rgba(255,255,255,0.72)"
            letterSpacing="0.02em"
          >
            {audience}
          </Text>
        ) : null}

        <Box
          role="group"
          aria-label="Data e orario del concerto"
          borderRadius="lg"
          bg={MUNLIGHT.dateBar}
          px={{ base: 4, md: 5 }}
          py={{ base: 3, md: 4 }}
          boxShadow="0 4px 20px rgba(0,0,0,0.2)"
        >
          <Text
            fontSize="xs"
            fontWeight="700"
            letterSpacing="0.14em"
            textTransform="uppercase"
            color="rgba(255,255,255,0.92)"
            mb={2}
          >
            Data
          </Text>
          <HStack align="flex-start" spacing={3}>
            <Icon
              as={FaCalendarAlt}
              flexShrink={0}
              mt={1}
              boxSize={{ base: 6, md: 7 }}
              color={MUNLIGHT.white}
              opacity={0.95}
              aria-hidden
            />
            <Box>
              <Text
                display="block"
                fontSize={{ base: 'xl', md: '2xl' }}
                fontWeight="700"
                lineHeight="short"
                color={MUNLIGHT.white}
                letterSpacing="0.02em"
                textTransform="uppercase"
              >
                {dateLabel}
              </Text>
              {timeLabel ? (
                <Text
                  mt={1}
                  fontSize={{ base: 'md', md: 'lg' }}
                  fontWeight="600"
                  color="rgba(255,255,255,0.9)"
                >
                  Ore {timeLabel}
                </Text>
              ) : null}
            </Box>
          </HStack>
        </Box>

        <VStack align="stretch" spacing={4}>
          {paragraphs.map((p) => (
            <Text
              key={`${concertId}-${p.slice(0, 48)}-${p.length}`}
              fontSize={{ base: 'md', md: 'md' }}
              lineHeight="tall"
              color="rgba(255,255,255,0.82)"
            >
              {p}
            </Text>
          ))}
        </VStack>

        {formation?.length > 0 ? (
          <Box>
            <Text
              fontSize="sm"
              fontWeight="600"
              color={MUNLIGHT.accentSoft}
              mb={2}
              textTransform="uppercase"
              letterSpacing="0.08em"
            >
              Formazione
            </Text>
            <UnorderedList
              spacing={1}
              pl={1}
              color="rgba(255,255,255,0.9)"
              fontSize="md"
              lineHeight="tall"
            >
              {formation.map((line) => (
                <ListItem key={`${concertId}-${line}`} ml={3}>
                  {line}
                </ListItem>
              ))}
            </UnorderedList>
          </Box>
        ) : null}

        {ctaHref ? (
          ctaIsHash ? (
            <Button
              as="a"
              href={ctaHref}
              variant="outline"
              alignSelf="flex-start"
              size="md"
              borderWidth="2px"
              borderColor="rgba(255,255,255,0.55)"
              color={MUNLIGHT.white}
              fontWeight="600"
              _hover={{
                bg: 'rgba(255,255,255,0.1)',
                borderColor: MUNLIGHT.accentSoft,
                color: MUNLIGHT.white,
              }}
              _active={{ bg: 'rgba(255,255,255,0.15)' }}
            >
              {ctaLabel}
            </Button>
          ) : (
            <Button
              as="a"
              href={ctaHref}
              target="_blank"
              rel="noopener noreferrer"
              variant="outline"
              alignSelf="flex-start"
              size="md"
              borderWidth="2px"
              borderColor="rgba(255,255,255,0.55)"
              color={MUNLIGHT.white}
              fontWeight="600"
              _hover={{
                bg: 'rgba(255,255,255,0.1)',
                borderColor: MUNLIGHT.accentSoft,
              }}
            >
              {ctaLabel}
            </Button>
          )
        ) : null}
      </VStack>
    </Box>
  );
}

export default MunlightConcertCard;
